import { useHistory, useRef } from "react-router-dom";
import Pricing from "./Pricing";
import { lazy, useEffect, useState } from "react";
import Features from "./Features";
import { getJWT, removeJWT } from "../utils";
import VideoRecorder from "./VideoRecorder";
import Draggable from "react-draggable";

function App({ focus }) {
  const history = useHistory();
  const scrollToPricing = () => {
    document.getElementById("pricing").scrollIntoView({ behavior: "smooth" });
  };

  const scrollToFeatures = () => {
    document.getElementById("features").scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (focus === "features") {
      scrollToFeatures();
    } else if (focus === "pricing") {
      scrollToPricing();
    }
  }, [focus]);

  return (
    <div className="flex flex-col min-h-screen">
      <header className="fixed z-50 w-full px-4 bg-background/80 md:px-8 backdrop-blur">
        <div className="flex max-w-[1100px] mx-auto items-center justify-between py-4 h-18">
          <div className="flex items-center gap-4 md:gap-10">
            <a className="items-center hidden space-x-2 md:flex" href="/">
              <span className="font-bold sm:inline-block">Diplom.org</span>
            </a>
            <nav className="hidden gap-6 md:flex">
              <a
                className="flex items-center text-lg font-medium transition-colors hover:text-foreground/80 sm:text-sm text-foreground/60"
                href="/"
              >
                Home
              </a>
              <a
                className="flex items-center text-lg font-medium transition-colors hover:text-foreground/80 sm:text-sm text-foreground/60"
                href="/#features"
              >
                Features
              </a>
              <a
                className="flex items-center text-lg font-medium transition-colors hover:text-foreground/80 sm:text-sm text-foreground/60"
                href="/#pricing"
              >
                Pricing
              </a>
            </nav>
            <button className="inline-flex items-center justify-center space-x-2 text-sm font-medium transition-colors rounded-md whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 w-9 md:hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-6 h-6 lucide lucide-menu"
              >
                <line x1="4" x2="20" y1="12" y2="12"></line>
                <line x1="4" x2="20" y1="6" y2="6"></line>
                <line x1="4" x2="20" y1="18" y2="18"></line>
              </svg>
            </button>
            <a className="items-center space-x-2 md:hidden" href="/">
              <span className="font-bold sm:inline-block">Diplom.org</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <nav className="items-center hidden gap-4 md:flex">
              <a
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium transition-colors rounded-md shadow whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9"
                href="/sign-in"
              >
                Sign In
              </a>
            </nav>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <section className="py-32 space-y-6 md:py-48 lg:py-52">
          <div className="container flex max-w-[50rem] flex-col items-center gap-4 text-center">
            <div className="rounded-2xl bg-muted px-4 py-1.5 text-sm font-medium">
              <svg
                fill="currentColor"
                width="23px"
                height="23px"
                className="inline-block mx-auto"
                focusable="false"
                viewBox="0 0 24 24"
                aria-hidden="true"
                style={{ marginTop: "-3px" }}
              >
                <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"></path>
              </svg>
              {` `}
              Video Verified
            </div>
            <h1 className="text-3xl font-heading sm:text-5xl lg:text-7xl">
              Diploma in English Proficiency
            </h1>
            <p className="max-w-[42rem] leading-normal text-muted-foreground sm:text-xl sm:leading-8">
              All you need is a computer with a webcam and a headset.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                className="inline-flex items-center justify-center h-10 px-8 text-sm font-medium transition-colors rounded-md shadow whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90"
                href="/tests"
              >
                Get Started
              </a>
              {/* <a
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center h-10 px-8 text-sm font-medium transition-colors border rounded-md shadow-sm whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border-input bg-background hover:bg-accent hover:text-accent-foreground"
                href="https://github.com/stack-auth/stack-template"
              >
                GitHub
              </a> */}
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Powered by{` `}
              <a
                href="https://IELTSing.com"
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                IELTSing
              </a>
            </p>
          </div>
        </section>
        <div id="features"></div>
        <section
          id="features"
          className="container py-8 space-y-6 md:py-12 lg:py-24"
        >
          <div className="flex flex-col items-center max-w-6xl mx-auto space-y-4 text-center">
            <h2 className="text-3xl font-semibold md:text-4xl">Features</h2>
            <p className="max-w-[85%] text-muted-foreground sm:text-lg">
              Reliable English assessment tools trusted by recruiters worldwide.
            </p>
          </div>
          <div className="grid justify-center gap-4 mx-auto sm:grid-cols-2 md:max-w-5xl md:grid-cols-3">
            <div className="relative p-2 overflow-hidden border rounded-lg bg-background">
              <div className="flex h-[180px] flex-col rounded-md p-6 gap-4">
                <svg
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-12 h-12 fill-current"
                >
                  <path
                    d="M2 1C1.44772 1 1 1.44772 1 2V13C1 13.5523 1.44772 14 2 14H13C13.5523 14 14 13.5523 14 13V2C14 1.44772 13.5523 1 13 1H2ZM3.05 6H4.95V12H3.05V6ZM5.075 4.005C5.075 4.59871 4.59371 5.08 4 5.08C3.4063 5.08 2.925 4.59871 2.925 4.005C2.925 3.41129 3.4063 2.93 4 2.93C4.59371 2.93 5.075 3.41129 5.075 4.005ZM12 8.35713C12 6.55208 10.8334 5.85033 9.67449 5.85033C9.29502 5.83163 8.91721 5.91119 8.57874 6.08107C8.32172 6.21007 8.05265 6.50523 7.84516 7.01853H7.79179V6.00044H6V12.0047H7.90616V8.8112C7.8786 8.48413 7.98327 8.06142 8.19741 7.80987C8.41156 7.55832 8.71789 7.49825 8.95015 7.46774H9.02258C9.62874 7.46774 10.0786 7.84301 10.0786 8.78868V12.0047H11.9847L12 8.35713Z"
                    fill="currentColor"
                    fillRule="evenodd"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="space-y-2">
                  <h3 className="font-bold">LinkedIn Integration</h3>
                  <p className="text-sm text-muted-foreground">
                    Easily showcase your certificate on LinkedIn for recruiters.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative p-2 overflow-hidden border rounded-lg bg-background">
              <div className="flex h-[180px] flex-col rounded-md p-6 gap-4">
                <svg
                  viewBox="0 0 22 22"
                  fill="currentColor"
                  className="w-12 h-12 fill-current"
                >
                  <path
                    d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"
                    fill="currentColor"
                  ></path>
                </svg>
                <div className="space-y-2">
                  <h3 className="font-bold">Verified Assessment</h3>
                  <p className="text-sm text-muted-foreground">
                    Video-verified tests ensure fairness and skill authenticity.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative p-2 overflow-hidden border rounded-lg bg-background">
              <div className="flex h-[180px] flex-col rounded-md p-6 gap-4">
                <svg
                  fill="#000000"
                  viewBox="0 0 431.17 431.17"
                  className="w-12 h-12 fill-current"
                >
                  <g>
                    <path
                      d="M68.831,99.602h255.814c7.878,0,14.265-6.387,14.265-14.265c0-7.878-6.387-14.265-14.265-14.265H68.831
		c-7.878,0-14.265,6.387-14.265,14.265C54.566,93.215,60.953,99.602,68.831,99.602z"
                    />
                    <path
                      d="M68.831,160.465h255.814c7.878,0,14.265-6.387,14.265-14.265c0-7.878-6.387-14.265-14.265-14.265H68.831
		c-7.878,0-14.265,6.387-14.265,14.265C54.566,154.078,60.953,160.465,68.831,160.465z"
                    />
                    <path
                      d="M204.549,192.799H68.831c-7.878,0-14.265,6.387-14.265,14.265c0,7.878,6.387,14.265,14.265,14.265h135.718
		c7.878,0,14.265-6.387,14.265-14.265C218.814,199.186,212.427,192.799,204.549,192.799z"
                    />
                    <path
                      d="M430.373,409.679l-37.295-73.843l14.431-5.759c3.79-1.513,6.97-5.888,7.24-9.96l1.229-18.516
		c0.206-3.102,1.778-7.941,3.434-10.569l9.886-15.702c2.175-3.452,2.175-8.861,0.001-12.314l-9.887-15.702
		c-1.654-2.628-3.227-7.469-3.433-10.569l-1.228-18.516c-0.232-3.486-2.6-7.188-5.652-9.132V37.438
		c0-15.732-12.798-28.53-28.529-28.53H28.529C12.798,8.908,0,21.706,0,37.438v244.381c0,15.732,12.798,28.529,28.529,28.529h224.396
		l0.648,9.77c0.271,4.072,3.45,8.446,7.24,9.959l13.549,5.407l-37.474,74.195c-1.153,2.284-0.844,3.66-0.381,4.412
		c0.293,0.477,1.011,1.275,2.523,1.275c0.756,0,1.627-0.204,2.589-0.606l30.697-12.852c0.755-0.316,1.653-0.483,2.597-0.483
		c2.255,0,4.553,0.933,5.854,2.374l14.746,16.349c1.229,1.363,2.751,2.114,4.283,2.114c2.022,0,3.836-1.307,4.85-3.494l28.89-62.32
		c0.063-0.001,0.125-0.001,0.188-0.002l28.891,62.322c1.014,2.188,2.827,3.494,4.851,3.494c1.531,0,3.053-0.751,4.282-2.115
		l14.745-16.347c1.302-1.442,3.6-2.375,5.854-2.375c0.943,0,1.842,0.167,2.597,0.483l30.697,12.852
		c0.962,0.402,1.833,0.606,2.589,0.606c1.513,0,2.23-0.799,2.523-1.275C431.217,413.339,431.526,411.963,430.373,409.679z
		 M28.529,37.438h352.039l0.002,156.56l-11.152-13.417c-2.036-2.449-5.764-4.094-9.275-4.094c-0.855,0-1.673,0.098-2.433,0.291
		l-17.998,4.581c-2.867,0.729-8.236,0.729-11.104,0l-17.996-4.581c-0.76-0.193-1.578-0.291-2.433-0.291
		c-3.512,0-7.239,1.645-9.275,4.094l-11.866,14.277c-1.984,2.389-6.099,5.379-8.983,6.53l-17.24,6.882
		c-3.791,1.513-6.971,5.887-7.241,9.959l-1.229,18.516c-0.205,3.102-1.777,7.941-3.433,10.569l-9.886,15.702
		c-2.175,3.452-2.175,8.862,0,12.314l4.085,6.488H28.529V37.438z M334.16,329.825c-33.497,0-60.652-27.154-60.652-60.652
		c0-33.498,27.155-60.652,60.652-60.652c33.498,0,60.654,27.154,60.654,60.652C394.815,302.671,367.658,329.825,334.16,329.825z"
                    />
                  </g>
                </svg>

                <div className="space-y-2">
                  <h3 className="font-bold">IELTS &amp; TOEFL format</h3>
                  <p className="text-sm text-muted-foreground">
                    Real IELTS & TOEFL exam format for accurate assessment.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative p-2 overflow-hidden border rounded-lg bg-background">
              <div className="flex h-[180px] flex-col rounded-md p-6 gap-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-12 h-12 lucide lucide-users"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <div className="space-y-2">
                  <h3 className="font-bold">350K+ Users</h3>
                  <p className="text-sm text-muted-foreground">
                    Trusted by 350K users with 5+ years of proven results.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative p-2 overflow-hidden border rounded-lg bg-background">
              <div className="flex h-[180px] flex-col rounded-md p-6 gap-4">
                <svg
                  fill="currentColor"
                  className="w-12 h-12 fill-current"
                  focusable="false"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"></path>
                </svg>

                <div className="space-y-2">
                  <h3 className="font-bold">Screening Interview</h3>
                  <p className="text-sm text-muted-foreground">
                    Answer screening questions on video - save recruiter time.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative p-2 overflow-hidden border rounded-lg bg-background">
              <div className="flex h-[180px] flex-col rounded-md p-6 gap-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-12 h-12 lucide lucide-component"
                >
                  <path d="M15.536 11.293a1 1 0 0 0 0 1.414l2.376 2.377a1 1 0 0 0 1.414 0l2.377-2.377a1 1 0 0 0 0-1.414l-2.377-2.377a1 1 0 0 0-1.414 0z"></path>
                  <path d="M2.297 11.293a1 1 0 0 0 0 1.414l2.377 2.377a1 1 0 0 0 1.414 0l2.377-2.377a1 1 0 0 0 0-1.414L6.088 8.916a1 1 0 0 0-1.414 0z"></path>
                  <path d="M8.916 17.912a1 1 0 0 0 0 1.415l2.377 2.376a1 1 0 0 0 1.414 0l2.377-2.376a1 1 0 0 0 0-1.415l-2.377-2.376a1 1 0 0 0-1.414 0z"></path>
                  <path d="M8.916 4.674a1 1 0 0 0 0 1.414l2.377 2.376a1 1 0 0 0 1.414 0l2.377-2.376a1 1 0 0 0 0-1.414l-2.377-2.377a1 1 0 0 0-1.414 0z"></path>
                </svg>
                <div className="space-y-2">
                  <h3 className="font-bold">All Parts of the Exam</h3>
                  <p className="text-sm text-muted-foreground">
                    Covers all 4 IELTS parts: Speaking, Listening, Reading,
                    Writing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div id="pricing"></div>
        <section
          id="features"
          className="container py-8 space-y-6 md:py-12 lg:py-24"
        >
          <div className="flex flex-col items-center max-w-6xl mx-auto space-y-4 text-center">
            <h2 className="text-3xl font-semibold md:text-4xl">Pricing</h2>
            <p className="max-w-[85%] text-muted-foreground sm:text-lg">
              Flexible plans for everyone.
            </p>
          </div>
          <div className="grid justify-center gap-4 mx-auto sm:grid-cols-2 md:max-w-5xl md:grid-cols-3">
            <div className="w-full max-w-sm border shadow rounded-xl bg-card text-card-foreground">
              <div className="flex flex-col space-y-1.5 p-6">
                <h3 className="text-2xl font-bold tracking-tight">Basic</h3>
                <p className="text-sm text-muted-foreground">
                  For fast evaluation.
                </p>
              </div>
              <div className="p-6 pt-0">
                <div className="mb-4">
                  <span className="text-4xl font-bold">Free</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Quick 30-min Assessment</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Cam Video Recording</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Assessment Report</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>LinkedIn Integration</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>No credit card required</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Screening Interview</span>
                  </li>
                </ul>
              </div>
              <div className="flex items-center p-6 pt-0">
                <a
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium transition-colors border rounded-md shadow-sm whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border-input bg-background hover:bg-accent hover:text-accent-foreground h-9"
                  href="/tests"
                >
                  Get Started
                </a>
              </div>
            </div>

            <div className="w-full max-w-sm border-2 shadow-lg rounded-xl bg-card text-card-foreground border-primary">
              <div className="flex flex-col space-y-1.5 p-6">
                <h3 className="text-2xl font-bold tracking-tight">Pro</h3>
                <p className="text-sm text-muted-foreground">
                  Ideal for IELTS and TOEFL preparation.
                </p>
              </div>
              <div className="p-6 pt-0">
                <div className="mb-4">
                  <span className="text-4xl font-bold">
                    <s>$30</s> Free
                  </span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Quick 30-min Assessment</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>IELTS Format Assessment</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Cam Video Recording</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Premium Assessment Report</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>LinkedIn Integration</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>No credit card required</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Screening Interview</span>
                  </li>
                </ul>
              </div>
              <div className="flex items-center p-6 pt-0">
                <a
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium transition-colors rounded-md shadow whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9"
                  href="/tests"
                >
                  Upgrade to Pro
                </a>
              </div>
            </div>

            <div className="w-full max-w-sm border shadow rounded-xl bg-card text-card-foreground">
              <div className="flex flex-col space-y-1.5 p-6">
                <h3 className="text-2xl font-bold tracking-tight">
                  Enterprise
                </h3>
                <p className="text-sm text-muted-foreground">
                  For large organizations.
                </p>
              </div>
              <div className="p-6 pt-0">
                <div className="mb-4">
                  <span className="text-4xl font-bold">$500</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Bulk User Management</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Team Performance Analytics</span>
                  </li>

                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Unlimited Exam Access</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>API Access</span>
                  </li>
                  <li className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 mr-2 lucide lucide-check text-primary"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                    <span>Priority Support</span>
                  </li>
                </ul>
              </div>
              <div className="flex items-center p-6 pt-0">
                <a
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium transition-colors border rounded-md shadow-sm whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border-input bg-background hover:bg-accent hover:text-accent-foreground h-9"
                  href="mailto:sales@diplom.org"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <footer className="border-t max-w-[1100px] mx-auto w-[100%]">
        <div className="container flex flex-col items-center justify-between gap-4 py-10 md:h-24 md:flex-row md:py-0">
          <div className="flex flex-col items-center gap-4 px-8 md:flex-row md:gap-2 md:px-0">
            <p className="text-sm leading-loose text-center text-muted-foreground md:text-left">
              Copyright © 2025 Diplom.org
            </p>
          </div>
          <div className="flex items-center space-x-1">
            <a
              className="inline-flex items-center justify-center text-sm font-medium transition-colors rounded-md whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 w-9"
              href="https://www.linkedin.com/company/diplom-org"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
              >
                <path
                  d="M2 1C1.44772 1 1 1.44772 1 2V13C1 13.5523 1.44772 14 2 14H13C13.5523 14 14 13.5523 14 13V2C14 1.44772 13.5523 1 13 1H2ZM3.05 6H4.95V12H3.05V6ZM5.075 4.005C5.075 4.59871 4.59371 5.08 4 5.08C3.4063 5.08 2.925 4.59871 2.925 4.005C2.925 3.41129 3.4063 2.93 4 2.93C4.59371 2.93 5.075 3.41129 5.075 4.005ZM12 8.35713C12 6.55208 10.8334 5.85033 9.67449 5.85033C9.29502 5.83163 8.91721 5.91119 8.57874 6.08107C8.32172 6.21007 8.05265 6.50523 7.84516 7.01853H7.79179V6.00044H6V12.0047H7.90616V8.8112C7.8786 8.48413 7.98327 8.06142 8.19741 7.80987C8.41156 7.55832 8.71789 7.49825 8.95015 7.46774H9.02258C9.62874 7.46774 10.0786 7.84301 10.0786 8.78868V12.0047H11.9847L12 8.35713Z"
                  fill="currentColor"
                  fillRule="evenodd"
                  clipRule="evenodd"
                />
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
  //     <div className="relative top-0 overflow-y-scroll md:h-screen md:snap-y md:snap-mandatory">
  //       <div style={{ display: "none" }}>
  //         <h1>English test with free certification online</h1>
  //       </div>
  //       <section className="bg-white md:min-h-screen snap-start">
  //         <div className="relative md:h-screen max-w-[1200px] mx-auto">
  //           <div className="absolute left-0 right-0 z-20 flex flex-row items-start justify-center px-8 text-black sm:justify-between top-6">
  //             <a
  //               href="/"
  //               style={{
  //                 fontSize: "22px",
  //                 fontWeight: "400",
  //                 letterSpacing: "0.3px",
  //                 marginTop: "-7px",
  //               }}
  //             >
  //               Diplom.org
  //             </a>

  //             <div className="flex-row items-center justify-center hidden gap-8 text-black sm:flex">
  //               <a
  //                 href="#features"
  //                 onClick={(e) => {
  //                   e.preventDefault();
  //                   scrollToFeatures();
  //                 }}
  //                 className="hover:underline hover:text-primary"
  //               >
  //                 Features
  //               </a>
  //               <a
  //                 href="#pricing"
  //                 onClick={(e) => {
  //                   e.preventDefault();
  //                   scrollToPricing();
  //                 }}
  //                 className="hover:underline hover:text-primary"
  //               >
  //                 Pricing
  //               </a>
  //               <a href="/account" className="hover:underline hover:text-primary">
  //                 Account
  //               </a>
  //               {getJWT() && (
  //                 <a
  //                   onClick={(e) => {
  //                     e.preventDefault();
  //                     removeJWT();
  //                     window.dispatchEvent(
  //                       new CustomEvent("LOGOUT", { detail: {} })
  //                     );
  //                     window.location.reload();
  //                   }}
  //                   href="#log-out"
  //                   className="hover:underline hover:text-primary"
  //                 >
  //                   Log Out
  //                 </a>
  //               )}
  //             </div>
  //           </div>

  //           <div class="z-2 relative md:h-[100%] min-h-[600px] flex flex-col md:flex-row px-8">
  //             <div className="h-[55%] md:h-[100%] md:w-[40%] sm:flex justify-center ">
  //               <div class="h-full pt-20 md:pt-0 md:h-auto flex justify-center items-center relative">

  //                 <img
  //                   src="/static/diplom.png"
  //                   alt="Screen"
  //                   className=""
  //                   style={{
  //                     border: "1px solid #000",
  //                   }}
  //                 />
  //                 <svg
  //                   version="1.1"
  //                   id="Layer_1"
  //                   xmlns="http://www.w3.org/2000/svg"
  //                   x="0px"
  //                   y="0px"
  //                   viewBox="0 0 144 144"
  //                   width="110"
  //                   height="110"
  //                   className="playSvg"
  //                   onClick={(e) => {
  //                     e.preventDefault();
  //                     window
  //                       .open(
  //                         "https://www.youtube.com/watch?v=hUPGTywga7E",
  //                         "_blank"
  //                       )
  //                       .focus();
  //                   }}
  //                 >
  //                   <style type="text/css">{`
  // 	.st0z{fill:#ff0000;}
  // 	.st1z{fill:#FFFFFF;}
  //   .playSvg{
  //     position: absolute;
  //     left: 50%;
  //     top: 50%;
  //     margin-top: -60px;
  //     margin-left: -50px;
  //     opacity: 0;
  //     transition: all 150ms ease 0s;
  //     width: 100px;
  //      opacity: 1;
  //      cursor: pointer;
  //     }
  //         @media (max-width: 700px) {
  //          .playSvg{
  //              margin-top: -22px;
  //          }
  //         }

  // `}</style>
  //                   <path
  //                     d="M129.6,101.7c-1.2,6.3-6.3,11.1-12.6,12c-9.9,1.5-26.4,3.3-45,3.3c-18.3,0-34.8-1.8-45-3.3 c-6.3-0.9-11.4-5.7-12.6-12C13.2,94.8,12,84.6,12,72s1.2-22.8,2.4-29.7c1.2-6.3,6.3-11.1,12.6-12c9.9-1.5,26.4-3.3,45-3.3 s34.8,1.8,45,3.3c6.3,0.9,11.4,5.7,12.6,12c1.2,6.9,2.7,17.1,2.7,29.7C132,84.6,130.8,94.8,129.6,101.7z"
  //                     className="jsx-c79b1519f2bfba94 st0z"
  //                   ></path>
  //                   <path
  //                     d="M60,93V51l36,21L60,93z"
  //                     className="jsx-c79b1519f2bfba94 st1z"
  //                   ></path>
  //                 </svg>
  //               </div>
  //             </div>
  //             <div className="md:w-[7%]"></div>

  //             <div className="md:h-[100%] md:w-[50%] flex flex-col text-black justify-center p-5">
  //               <div className="mb-3 text-4xl text-center">
  //                 Diploma in English Proficiency
  //               </div>
  //               <div>
  //                 Verify your English proficiency with video-recorded assessments
  //                 for speaking and real-time writing checks. Save time with a
  //                 screening interview and seamlessly add your certification to
  //                 LinkedIn. Choose between a swift 30-minute evaluation or a full
  //                 IELTS/TOEFL-style test.
  //               </div>
  //               <div className="flex flex-col items-center gap-10 mt-10">
  //                 <a
  //                   class="cursor-pointer text-center track-btn relative mx-auto md:mx-0 flex justify-center w-72 items-center z-0 h-14 rounded-xl px-6 text-neutral-50 animate-scale opacity-100 transition duration-500 bg-secondary after:bg-secondary md:bg-primary md:after:bg-primary"
  //                   href="/tests"
  //                 >
  //                   {/* <img
  //                     class="w-8 h-8 mr-3"
  //                     alt="Google Chrome Store"
  //                     src="/static/chrome-store.svg"
  //                   /> */}
  //                   <svg
  //                     focusable="false"
  //                     viewBox="0 0 24 24"
  //                     aria-hidden="true"
  //                     width={24}
  //                     height={24}
  //                     style={{ margin: "-5px 7px -5px -5px" }}
  //                   >
  //                     <path
  //                       fill="currentColor"
  //                       d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"
  //                     ></path>
  //                   </svg>
  //                   Start English Assessment
  //                 </a>
  //                 <div class="flex flex-row items-center gap-2">
  //                   <div class="hidden sm:flex">
  //                     <img
  //                       class="avatar"
  //                       src="/static/avatars/1.jpg"
  //                       lazy="loaded"
  //                       alt="avatar"
  //                     />
  //                     <img
  //                       class="avatar"
  //                       src="/static/avatars/2.jpg"
  //                       lazy="loaded"
  //                       alt="avatar"
  //                     />
  //                     <img
  //                       class="avatar"
  //                       src="/static/avatars/3.jpg"
  //                       lazy="loaded"
  //                       alt="avatar"
  //                     />
  //                     <img
  //                       class="avatar"
  //                       src="/static/avatars/4.jpg"
  //                       lazy="loaded"
  //                       alt="avatar"
  //                     />
  //                     <div class="flex justify-center items-center font-medium avatar text-xs text-white bg-primary">
  //                       2K+
  //                     </div>
  //                   </div>
  //                   <div class="flex flex-col space-y-1 text-black md:text-black">
  //                     <div class="flex sm:justify-start justify-center items-center space-x-2">
  //                       <div class="flex">
  //                         <svg
  //                           xmlns="http://www.w3.org/2000/svg"
  //                           viewBox="0 0 24 24"
  //                           fill="currentColor"
  //                           aria-hidden="true"
  //                           data-slot="icon"
  //                           class="w-5 h-5 text-yellow-400"
  //                         >
  //                           <path
  //                             fill-rule="evenodd"
  //                             d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
  //                             clip-rule="evenodd"
  //                           ></path>
  //                         </svg>
  //                         <svg
  //                           xmlns="http://www.w3.org/2000/svg"
  //                           viewBox="0 0 24 24"
  //                           fill="currentColor"
  //                           aria-hidden="true"
  //                           data-slot="icon"
  //                           class="w-5 h-5 text-yellow-400"
  //                         >
  //                           <path
  //                             fill-rule="evenodd"
  //                             d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
  //                             clip-rule="evenodd"
  //                           ></path>
  //                         </svg>
  //                         <svg
  //                           xmlns="http://www.w3.org/2000/svg"
  //                           viewBox="0 0 24 24"
  //                           fill="currentColor"
  //                           aria-hidden="true"
  //                           data-slot="icon"
  //                           class="w-5 h-5 text-yellow-400"
  //                         >
  //                           <path
  //                             fill-rule="evenodd"
  //                             d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
  //                             clip-rule="evenodd"
  //                           ></path>
  //                         </svg>
  //                         <svg
  //                           xmlns="http://www.w3.org/2000/svg"
  //                           viewBox="0 0 24 24"
  //                           fill="currentColor"
  //                           aria-hidden="true"
  //                           data-slot="icon"
  //                           class="w-5 h-5 text-yellow-400"
  //                         >
  //                           <path
  //                             fill-rule="evenodd"
  //                             d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
  //                             clip-rule="evenodd"
  //                           ></path>
  //                         </svg>
  //                         <svg
  //                           xmlns="http://www.w3.org/2000/svg"
  //                           viewBox="0 0 24 24"
  //                           fill="currentColor"
  //                           aria-hidden="true"
  //                           data-slot="icon"
  //                           class="w-5 h-5 text-yellow-400"
  //                         >
  //                           <path
  //                             fill-rule="evenodd"
  //                             d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
  //                             clip-rule="evenodd"
  //                           ></path>
  //                         </svg>
  //                       </div>
  //                       <div className="text-black">
  //                         <strong>4.9</strong> / 5
  //                       </div>
  //                     </div>
  //                     <div className="text-black">
  //                       Trusted by <strong>2,518+</strong> users
  //                     </div>
  //                   </div>
  //                 </div>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </section>
  //       <Features isGetStarted={focus === "features"} />
  //       <Pricing />
  //     </div>
}

export default App;
